import type { CmsLocaleInputType } from "@workspace/casino_core/schema";
import {
  type FC,
  type PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import devConsole, { type DevConsoleState } from "./devConsole";
import { createGraphqlBackend } from "./graphqlBackend";
import live from "./live";
import rewardCenter from "./rewardCenter";
import type { RewardCenterState } from "./rewardCenter/types";
import createSystem, { type System } from "./system";
import { type Actors, RewardCenterContext } from "./useRewardCenter";

type Props = PropsWithChildren<{
  isDevConsoleEnabled: boolean;
  locale?: CmsLocaleInputType;
  site: string;
}>;

const RewardCenterProvider: FC<Props> = ({
  children,
  isDevConsoleEnabled,
  locale,
  site,
}) => {
  const [state, setState] = useState(rewardCenter.init);
  const [devConsoleState, setDevConsoleState] = useState(devConsole.init);
  const systemRef = useRef<System<Actors> | undefined>(undefined);

  useEffect(() => {
    const system = createSystem<Actors>();
    system.spawn("rewardCenter", rewardCenter);
    const backend = createGraphqlBackend(locale);
    system.spawn("backend", {
      ...backend,
      init: { ...backend.init, site },
    });
    system.spawn("live", live);
    if (isDevConsoleEnabled) {
      system.spawn("devConsole", devConsole);
      system.onMessage((observed) => {
        if (observed.to === "devConsole") return;
        system.send("devConsole", { type: "observed", observed });
      });
      system.subscribe("devConsole", () =>
        setDevConsoleState(system.getState<DevConsoleState>("devConsole")),
      );
    }
    const unsubscribe = system.subscribe("rewardCenter", () =>
      setState(system.getState<RewardCenterState>("rewardCenter")),
    );
    systemRef.current = system;

    // Initial load
    system.send("backend", { type: "loadStatus" });
    system.send("backend", { type: "loadTierConfiguration" });
    system.send("backend", { type: "loadFaq" });

    return () => {
      systemRef.current = undefined;
      unsubscribe();
      system.stop();
    };
  }, [isDevConsoleEnabled, locale, site]);

  const send = useCallback<System<Actors>["send"]>(
    (to, msg) => systemRef.current?.send(to, msg),
    [],
  );

  const value = useMemo(
    () => ({ state, devConsole: devConsoleState, send }),
    [state, devConsoleState, send],
  );

  return (
    <RewardCenterContext.Provider value={value}>
      {children}
    </RewardCenterContext.Provider>
  );
};

export default RewardCenterProvider;
